// Observable Market Data Analytics - Phase 1
// No execution assumptions - pure market observation

import type { OrderBookEntry, Trade } from '../types';

// Enhanced types for observable analytics
export interface BookSnapshot {
  timestamp: number;
  bestBid: OrderBookEntry | null;
  bestAsk: OrderBookEntry | null;
  spread: number;
  depth5: { bidVolume: number; askVolume: number; totalVolume: number };
  depth10: { bidVolume: number; askVolume: number; totalVolume: number };
  imbalance: number;
  midPrice: number;
}

export interface BookAnalytics {
  spreadStability: number; // 0-1, how stable the spread has been
  depthPersistence: number; // 0-1, how persistent order book levels are
  imbalanceShifts: number; // frequency of imbalance changes
  priceLevelUpdates: {
    frequency: number; // updates per minute
    persistenceRate: number; // how long levels persist
    averageLifetime: number; // average level lifetime in seconds
  };
}

export interface TradeWindow {
  startTime: number;
  endTime: number;
  trades: Trade[];
  priceAtStart: number;
  priceAtEnd: number;
  totalVolume: number;
  buyVolume: number;
  sellVolume: number;
  netFlow: number;
  averageTradeSize: number;
  tradeCount: number;
}

export interface TradePatterns {
  aggressorRatio: number; // ratio of aggressive vs passive trades
  sizeDistribution: {
    small: number; // < 500 shares
    medium: number; // 500-2000 shares
    large: number; // > 2000 shares
    averageSize: number;
    medianSize: number;
  };
  timingPatterns: {
    frequency: number; // trades per minute
    burstiness: number; // clustering measure
    regularityScore: number; // how regular the timing is
  };
  priceImpactObservations: PriceImpactObservations;
  tradeClusters: TradeCluster[];
}

export interface PriceImpactObservations {
  averageImpactPerVolume: number; // average price change per unit volume
  volumePriceCorrelation: number; // correlation between volume and price moves
  flowImpactCorrelation: number; // correlation between net flow and price
  temporaryImpactRatio: number; // how much impact is temporary
  observations: Array<{
    volume: number;
    netFlow: number;
    priceChange: number;
    priceChangePercent: number;
    impactPerVolume: number;
    timestamp: number;
  }>;
}

export interface TradeCluster {
  startTime: number;
  endTime: number;
  tradeCount: number;
  totalVolume: number;
  netFlow: number;
  priceChange: number;
  clusterType: 'momentum' | 'reversal' | 'neutral';
}

export interface MarketActivityProfile {
  averageTradesPerMinute: number;
  averageBookUpdatesPerMinute: number;
  volumeProfile: {
    hourly: Map<number, number>; // hour -> average volume
    bySize: Map<string, number>; // size bucket -> count
  };
  priceVelocity: {
    average: number; // average price change per minute
    volatility: number; // standard deviation of price changes
  };
  marketRegime: 'active' | 'quiet' | 'volatile' | 'stable';
}

// Book State Tracker - tracks order book changes over time
export class BookStateTracker {
  private bookHistory: BookSnapshot[] = [];
  private readonly MAX_HISTORY = 10000; // ~3 hours at 1 update/second
  private lastAnalytics: BookAnalytics | null = null;
  private lastAnalyticsTime = 0;
  private readonly ANALYTICS_CACHE_MS = 30000; // Cache for 30 seconds

  trackBookUpdate(
    orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] },
    timestamp: number = Date.now()
  ): void {
    const snapshot = this.createSnapshot(orderBook, timestamp);

    this.bookHistory.push(snapshot);
    if (this.bookHistory.length > this.MAX_HISTORY) {
      this.bookHistory.shift(); // Remove oldest
    }

    // Log significant changes
    if (this.bookHistory.length > 1) {
      const prev = this.bookHistory[this.bookHistory.length - 2];
      this.logSignificantChanges(prev, snapshot);
    }
  }

  private createSnapshot(
    orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] },
    timestamp: number
  ): BookSnapshot {
    const bestBid = orderBook.bids[0] || null;
    const bestAsk = orderBook.asks[0] || null;
    const spread = bestBid && bestAsk ? bestAsk.price - bestBid.price : 0;
    const midPrice = bestBid && bestAsk ? (bestBid.price + bestAsk.price) / 2 : 0;

    return {
      timestamp,
      bestBid,
      bestAsk,
      spread,
      depth5: this.calculateDepth(orderBook, 5),
      depth10: this.calculateDepth(orderBook, 10),
      imbalance: this.calculateImbalance(orderBook),
      midPrice
    };
  }

  private calculateDepth(
    orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] },
    levels: number
  ): { bidVolume: number; askVolume: number; totalVolume: number } {
    const bidVolume = orderBook.bids
      .slice(0, levels)
      .reduce((sum, bid) => sum + bid.size, 0);

    const askVolume = orderBook.asks
      .slice(0, levels)
      .reduce((sum, ask) => sum + ask.size, 0);

    return {
      bidVolume,
      askVolume,
      totalVolume: bidVolume + askVolume
    };
  }

  private calculateImbalance(
    orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] }
  ): number {
    const depth = this.calculateDepth(orderBook, 5);
    return depth.totalVolume > 0 ?
      (depth.bidVolume - depth.askVolume) / depth.totalVolume : 0;
  }

  private logSignificantChanges(prev: BookSnapshot, current: BookSnapshot): void {
    // Log spread changes > 10%
    if (prev.spread > 0 && Math.abs(current.spread - prev.spread) / prev.spread > 0.1) {
      // console.log(`📊 Significant spread change: ${prev.spread.toFixed(2)} → ${current.spread.toFixed(2)}`);
    }

    // Log imbalance shifts > 20%
    if (Math.abs(current.imbalance - prev.imbalance) > 0.2) {
      // console.log(`⚖️ Imbalance shift: ${(prev.imbalance * 100).toFixed(1)}% → ${(current.imbalance * 100).toFixed(1)}%`);
    }

    // Log depth changes > 25%
    const depthChange = Math.abs(current.depth5.totalVolume - prev.depth5.totalVolume) /
                       Math.max(prev.depth5.totalVolume, 1);
    if (depthChange > 0.25) {
      // console.log(`📈 Depth change: ${prev.depth5.totalVolume.toLocaleString()} → ${current.depth5.totalVolume.toLocaleString()}`);
    }
  }

  getRecentAnalytics(forceRecalculate: boolean = false): BookAnalytics {
    const now = Date.now();

    // Return cached analytics if recent
    if (!forceRecalculate &&
        this.lastAnalytics &&
        (now - this.lastAnalyticsTime) < this.ANALYTICS_CACHE_MS) {
      return this.lastAnalytics;
    }

    this.lastAnalytics = this.calculateBookAnalytics();
    this.lastAnalyticsTime = now;
    return this.lastAnalytics;
  }

  private calculateBookAnalytics(): BookAnalytics {
    if (this.bookHistory.length < 10) {
      return this.getDefaultAnalytics();
    }

    const recent = this.bookHistory.slice(-100); // Last 100 snapshots

    return {
      spreadStability: this.calculateSpreadStability(recent),
      depthPersistence: this.calculateDepthPersistence(recent),
      imbalanceShifts: this.calculateImbalanceShifts(recent),
      priceLevelUpdates: this.analyzePriceLevelUpdates(recent)
    };
  }

  private calculateSpreadStability(snapshots: BookSnapshot[]): number {
    if (snapshots.length < 2) return 0;

    const spreads = snapshots.map(s => s.spread).filter(s => s > 0);
    if (spreads.length < 2) return 0;

    const avgSpread = spreads.reduce((sum, s) => sum + s, 0) / spreads.length;
    const variance = spreads.reduce((sum, s) => sum + Math.pow(s - avgSpread, 2), 0) / spreads.length;
    const coefficientOfVariation = Math.sqrt(variance) / avgSpread;

    // Return stability score (0 = very volatile, 1 = very stable)
    return Math.max(0, Math.min(1, 1 - coefficientOfVariation));
  }

  private calculateDepthPersistence(snapshots: BookSnapshot[]): number {
    if (snapshots.length < 2) return 0;

    let persistentIntervals = 0;
    let totalIntervals = 0;

    for (let i = 1; i < snapshots.length; i++) {
      const prev = snapshots[i - 1];
      const curr = snapshots[i];

      totalIntervals++;

      // Consider depth persistent if change is < 15%
      const depthChange = Math.abs(curr.depth5.totalVolume - prev.depth5.totalVolume) /
                         Math.max(prev.depth5.totalVolume, 1);

      if (depthChange < 0.15) {
        persistentIntervals++;
      }
    }

    return totalIntervals > 0 ? persistentIntervals / totalIntervals : 0;
  }

  private calculateImbalanceShifts(snapshots: BookSnapshot[]): number {
    if (snapshots.length < 2) return 0;

    let significantShifts = 0;

    for (let i = 1; i < snapshots.length; i++) {
      const prev = snapshots[i - 1];
      const curr = snapshots[i];

      // Count shifts > 20% as significant
      if (Math.abs(curr.imbalance - prev.imbalance) > 0.2) {
        significantShifts++;
      }
    }

    const timeSpanMinutes = (snapshots[snapshots.length - 1].timestamp - snapshots[0].timestamp) / 60000;
    return timeSpanMinutes > 0 ? significantShifts / timeSpanMinutes : 0; // shifts per minute
  }

  private analyzePriceLevelUpdates(snapshots: BookSnapshot[]): BookAnalytics['priceLevelUpdates'] {
    if (snapshots.length < 2) {
      return { frequency: 0, persistenceRate: 0, averageLifetime: 0 };
    }

    let updates = 0;
    let levelChanges = 0;
    const levelLifetimes: number[] = [];

    for (let i = 1; i < snapshots.length; i++) {
      const prev = snapshots[i - 1];
      const curr = snapshots[i];

      updates++;

      // Check if best prices changed
      const bidChanged = prev.bestBid?.price !== curr.bestBid?.price;
      const askChanged = prev.bestAsk?.price !== curr.bestAsk?.price;

      if (bidChanged || askChanged) {
        levelChanges++;

        // Estimate level lifetime (simplified)
        const timeDiff = curr.timestamp - prev.timestamp;
        levelLifetimes.push(timeDiff);
      }
    }

    const timeSpanMinutes = (snapshots[snapshots.length - 1].timestamp - snapshots[0].timestamp) / 60000;
    const frequency = timeSpanMinutes > 0 ? updates / timeSpanMinutes : 0;
    const persistenceRate = updates > 0 ? 1 - (levelChanges / updates) : 0;
    const averageLifetime = levelLifetimes.length > 0 ?
      levelLifetimes.reduce((sum, lt) => sum + lt, 0) / levelLifetimes.length / 1000 : 0; // in seconds

    return {
      frequency,
      persistenceRate,
      averageLifetime
    };
  }

  private getDefaultAnalytics(): BookAnalytics {
    return {
      spreadStability: 0.5,
      depthPersistence: 0.5,
      imbalanceShifts: 0,
      priceLevelUpdates: {
        frequency: 0,
        persistenceRate: 0.5,
        averageLifetime: 30 // 30 seconds default
      }
    };
  }

  // Public getters
  getDataPointCount(): number {
    return this.bookHistory.length;
  }

  getTimeSpanHours(): number {
    if (this.bookHistory.length < 2) return 0;
    const first = this.bookHistory[0];
    const last = this.bookHistory[this.bookHistory.length - 1];
    return (last.timestamp - first.timestamp) / (1000 * 60 * 60);
  }

  getRecentSnapshots(count: number = 100): BookSnapshot[] {
    return this.bookHistory.slice(-count);
  }

  // Export data for analysis
  exportHistory(): BookSnapshot[] {
    return [...this.bookHistory];
  }
}

// Trade Pattern Analyzer - analyzes trade sequences and patterns
export class TradePatternAnalyzer {
  private tradeHistory: Trade[] = [];
  private readonly MAX_HISTORY = 5000; // Keep last 5000 trades
  private lastAnalytics: TradePatterns | null = null;
  private lastAnalyticsTime = 0;
  private readonly ANALYTICS_CACHE_MS = 60000; // Cache for 1 minute

  addTrade(trade: Trade): void {
    this.tradeHistory.push(trade);
    if (this.tradeHistory.length > this.MAX_HISTORY) {
      this.tradeHistory.shift();
    }

    // Log interesting patterns
    this.detectTradePatterns(trade);
  }

  private detectTradePatterns(newTrade: Trade): void {
    if (this.tradeHistory.length < 5) return;

    const recent = this.tradeHistory.slice(-5);

    // Detect large trades
    if (newTrade.size > 2000) {
      // console.log(`🐋 Large trade detected: ${newTrade.side.toUpperCase()} ${newTrade.size.toLocaleString()} @ ${newTrade.price}`);
    }

    // Detect trade clusters
    const recentMinute = recent.filter(t => {
      const tradeTime = this.parseTradeTime(t.time);
      const newTradeTime = this.parseTradeTime(newTrade.time);
      return Math.abs(tradeTime - newTradeTime) < 60000; // Within 1 minute
    });

    if (recentMinute.length >= 4) {
      const volume = recentMinute.reduce((sum, t) => sum + t.size, 0);
      // console.log(`⚡ Trade cluster: ${recentMinute.length} trades, ${volume.toLocaleString()} volume`);
    }
  }

  analyzeTradeSequences(windowMinutes: number = 5): TradePatterns {
    const now = Date.now();

    // Return cached analytics if recent
    if (this.lastAnalytics && (now - this.lastAnalyticsTime) < this.ANALYTICS_CACHE_MS) {
      return this.lastAnalytics;
    }

    this.lastAnalytics = this.calculateTradePatterns(windowMinutes);
    this.lastAnalyticsTime = now;
    return this.lastAnalytics;
  }

  private calculateTradePatterns(windowMinutes: number): TradePatterns {
    if (this.tradeHistory.length < 10) {
      return this.getDefaultTradePatterns();
    }

    const timeWindow = windowMinutes * 60 * 1000;
    const windows = this.createTimeWindows(timeWindow);

    return {
      aggressorRatio: this.calculateAggressorRatio(windows),
      sizeDistribution: this.analyzeSizeDistribution(this.tradeHistory),
      timingPatterns: this.analyzeTimingPatterns(this.tradeHistory),
      priceImpactObservations: this.observePriceImpacts(windows),
      tradeClusters: this.identifyTradeClusters(windows)
    };
  }

  private createTimeWindows(windowSize: number): TradeWindow[] {
    const windows: TradeWindow[] = [];
    const now = Date.now();

    // Create sliding windows over the last few hours
    for (let i = 0; i < 12; i++) { // 12 windows = 1 hour with 5-minute windows
      const endTime = now - (i * windowSize);
      const startTime = endTime - windowSize;

      const tradesInWindow = this.tradeHistory.filter(trade => {
        const tradeTime = this.parseTradeTime(trade.time);
        return tradeTime >= startTime && tradeTime < endTime;
      });

      if (tradesInWindow.length === 0) continue;

      const prices = tradesInWindow.map(t => t.price);
      const volumes = tradesInWindow.map(t => t.size);
      const buyVolume = tradesInWindow.filter(t => t.side === 'buy').reduce((sum, t) => sum + t.size, 0);
      const sellVolume = tradesInWindow.filter(t => t.side === 'sell').reduce((sum, t) => sum + t.size, 0);

      windows.push({
        startTime,
        endTime,
        trades: tradesInWindow,
        priceAtStart: prices[0] || 0,
        priceAtEnd: prices[prices.length - 1] || 0,
        totalVolume: volumes.reduce((sum, v) => sum + v, 0),
        buyVolume,
        sellVolume,
        netFlow: buyVolume - sellVolume,
        averageTradeSize: volumes.length > 0 ? volumes.reduce((sum, v) => sum + v, 0) / volumes.length : 0,
        tradeCount: tradesInWindow.length
      });
    }

    return windows;
  }

  private calculateAggressorRatio(windows: TradeWindow[]): number {
    if (windows.length === 0) return 0.5;

    // Simplified aggressor detection based on timing patterns
    let aggressorTrades = 0;
    let totalTrades = 0;

    windows.forEach(window => {
      window.trades.forEach((trade, index) => {
        totalTrades++;

        // Assume trades that are part of rapid sequences are more aggressive
        if (index > 0) {
          const prevTrade = window.trades[index - 1];
          const timeDiff = this.parseTradeTime(trade.time) - this.parseTradeTime(prevTrade.time);

          if (timeDiff < 5000) { // Within 5 seconds = likely aggressive
            aggressorTrades++;
          }
        }
      });
    });

    return totalTrades > 0 ? aggressorTrades / totalTrades : 0.5;
  }

  private analyzeSizeDistribution(trades: Trade[]): TradePatterns['sizeDistribution'] {
    if (trades.length === 0) {
      return { small: 0, medium: 0, large: 0, averageSize: 0, medianSize: 0 };
    }

    const sizes = trades.map(t => t.size).sort((a, b) => a - b);
    const small = sizes.filter(s => s < 500).length;
    const medium = sizes.filter(s => s >= 500 && s <= 2000).length;
    const large = sizes.filter(s => s > 2000).length;

    const total = sizes.length;
    const averageSize = sizes.reduce((sum, s) => sum + s, 0) / total;
    const medianSize = sizes[Math.floor(total / 2)] || 0;

    return {
      small: total > 0 ? small / total : 0,
      medium: total > 0 ? medium / total : 0,
      large: total > 0 ? large / total : 0,
      averageSize,
      medianSize
    };
  }

  private analyzeTimingPatterns(trades: Trade[]): TradePatterns['timingPatterns'] {
    if (trades.length < 2) {
      return { frequency: 0, burstiness: 0, regularityScore: 0 };
    }

    // Calculate inter-trade intervals
    const intervals: number[] = [];
    for (let i = 1; i < trades.length; i++) {
      const curr = this.parseTradeTime(trades[i].time);
      const prev = this.parseTradeTime(trades[i - 1].time);
      intervals.push(curr - prev);
    }

    const avgInterval = intervals.reduce((sum, i) => sum + i, 0) / intervals.length;
    const frequency = avgInterval > 0 ? 60000 / avgInterval : 0; // trades per minute

    // Calculate burstiness (coefficient of variation of intervals)
    const variance = intervals.reduce((sum, i) => sum + Math.pow(i - avgInterval, 2), 0) / intervals.length;
    const burstiness = avgInterval > 0 ? Math.sqrt(variance) / avgInterval : 0;

    // Regularity score (inverse of burstiness)
    const regularityScore = Math.max(0, 1 - burstiness);

    return {
      frequency,
      burstiness,
      regularityScore
    };
  }

  private observePriceImpacts(windows: TradeWindow[]): PriceImpactObservations {
    const observations: PriceImpactObservations['observations'] = [];

    windows.forEach(window => {
      if (window.totalVolume === 0 || window.priceAtStart === 0) return;

      const priceChange = window.priceAtEnd - window.priceAtStart;
      const priceChangePercent = (priceChange / window.priceAtStart) * 100;
      const impactPerVolume = priceChange / window.totalVolume;

      observations.push({
        volume: window.totalVolume,
        netFlow: window.netFlow,
        priceChange,
        priceChangePercent,
        impactPerVolume,
        timestamp: window.endTime
      });
    });

    if (observations.length === 0) {
      return {
        averageImpactPerVolume: 0,
        volumePriceCorrelation: 0,
        flowImpactCorrelation: 0,
        temporaryImpactRatio: 0.6,
        observations: []
      };
    }

    const avgImpactPerVolume = observations.reduce((sum, obs) => sum + Math.abs(obs.impactPerVolume), 0) / observations.length;

    const volumePriceCorr = this.calculateCorrelation(
      observations.map(o => o.volume),
      observations.map(o => Math.abs(o.priceChange))
    );

    const flowImpactCorr = this.calculateCorrelation(
      observations.map(o => o.netFlow),
      observations.map(o => o.priceChange)
    );

    return {
      averageImpactPerVolume: avgImpactPerVolume,
      volumePriceCorrelation: volumePriceCorr,
      flowImpactCorrelation: flowImpactCorr,
      temporaryImpactRatio: 0.6, // Estimated - would need longer time series
      observations
    };
  }

  private identifyTradeClusters(windows: TradeWindow[]): TradeCluster[] {
    const clusters: TradeCluster[] = [];

    windows.forEach(window => {
      if (window.tradeCount < 3) return; // Need at least 3 trades for a cluster

      const priceChange = window.priceAtEnd - window.priceAtStart;
      const priceChangePercent = window.priceAtStart > 0 ? (priceChange / window.priceAtStart) * 100 : 0;

      let clusterType: TradeCluster['clusterType'] = 'neutral';
      if (Math.abs(priceChangePercent) > 0.1) {
        if (window.netFlow * priceChange > 0) {
          clusterType = 'momentum'; // Flow and price move in same direction
        } else {
          clusterType = 'reversal'; // Flow and price move in opposite direction
        }
      }

      clusters.push({
        startTime: window.startTime,
        endTime: window.endTime,
        tradeCount: window.tradeCount,
        totalVolume: window.totalVolume,
        netFlow: window.netFlow,
        priceChange,
        clusterType
      });
    });

    return clusters;
  }

  private calculateCorrelation(x: number[], y: number[]): number {
    if (x.length !== y.length || x.length < 2) return 0;

    const n = x.length;
    const sumX = x.reduce((sum, val) => sum + val, 0);
    const sumY = y.reduce((sum, val) => sum + val, 0);
    const sumXY = x.reduce((sum, val, i) => sum + val * y[i], 0);
    const sumXX = x.reduce((sum, val) => sum + val * val, 0);
    const sumYY = y.reduce((sum, val) => sum + val * val, 0);

    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumXX - sumX * sumX) * (n * sumYY - sumY * sumY));

    return denominator === 0 ? 0 : numerator / denominator;
  }

  private parseTradeTime(timeString: string): number {
    // Parse time string like "10:02:15" to timestamp
    const today = new Date();
    const [hours, minutes, seconds] = timeString.split(':').map(Number);
    return new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes, seconds).getTime();
  }

  private getDefaultTradePatterns(): TradePatterns {
    return {
      aggressorRatio: 0.5,
      sizeDistribution: {
        small: 0.6,
        medium: 0.3,
        large: 0.1,
        averageSize: 500,
        medianSize: 300
      },
      timingPatterns: {
        frequency: 1, // 1 trade per minute
        burstiness: 0.5,
        regularityScore: 0.5
      },
      priceImpactObservations: {
        averageImpactPerVolume: 0,
        volumePriceCorrelation: 0,
        flowImpactCorrelation: 0,
        temporaryImpactRatio: 0.6,
        observations: []
      },
      tradeClusters: []
    };
  }

  // Public getters
  getDataPointCount(): number {
    return this.tradeHistory.length;
  }

  getRecentPatterns(): TradePatterns {
    return this.analyzeTradeSequences(5);
  }

  getRecentTrades(count: number = 50): Trade[] {
    return this.tradeHistory.slice(-count);
  }
}

// Create singleton instances
export const bookStateTracker = new BookStateTracker();
export const tradePatternAnalyzer = new TradePatternAnalyzer();